import styled from '@emotion/styled/macro'
import { AppShell, FlexSpinner, RouteComponent } from 'components'
import { Text } from 'components/Typography'
import { Box, Flex } from 'grid'
import { themeColor } from 'mixins'
import { UserRole } from 'model'
import moment from 'moment'
import 'moment/locale/de'
import * as React from 'react'
import { default as ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useRoute } from 'react-router5'
import { AppBar } from './AppBar'
import { AppCommandBar } from './AppCommandBar'
import { AppNavigation } from './AppNavigation'
import GlobalContextProvider from './GlobalContextProvider'

const ErrorContainer = styled(Flex)`
  background: ${themeColor('redDark')};
  color: ${themeColor('white')};
`
moment.locale('de')

const ErrorComponent = ({ componentStack, error }: FallbackProps) => {
  const errorMessage = error
    ? ((error as any).err || error).toString().replace(/^Error: /, ' ')
    : 'null'

  return (
    <ErrorContainer flex="1" alignItems="center">
      <Box p="4">
        <Text font="xLarge" as="h2">
          Oops! An error occured!
        </Text>
        <Text font="mediumPlus" pt="3" pb="2">
          Here’s what we know...
        </Text>
        <Box as="pre">
          <strong>Error: </strong>
          {errorMessage}
          <br />
          <strong>Stacktrace:</strong> {componentStack}
        </Box>
      </Box>
    </ErrorContainer>
  )
}

export const App = () => {
  const { route } = useRoute()
  const appBar = <AppBar />
  const appNavigation = <AppNavigation />
  const appCommandBar = <AppCommandBar />

  return (
    <ErrorBoundary FallbackComponent={ErrorComponent}>
      <GlobalContextProvider>
        <AppShell
          appBar={appBar}
          appNavigation={appNavigation}
          appCommandBar={appCommandBar}
          roles={[
            UserRole.Hrg,
            UserRole.Maintenancehead,
            UserRole.ProvingOrganisation,
            UserRole.Reader,
            UserRole.ServiceTeam,
            UserRole.ProvingCreator,
            UserRole.User,
            UserRole.ResourceManager,
            UserRole.SystemAdministrator,
          ]}
        >
          <ErrorBoundary FallbackComponent={ErrorComponent} key={route.name}>
            <React.Suspense fallback={<FlexSpinner />}>
              <RouteComponent />
            </React.Suspense>
          </ErrorBoundary>
        </AppShell>
      </GlobalContextProvider>
    </ErrorBoundary>
  )
}
